import { Button, ButtonGroup, ModalProps } from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Column as TableColumn } from 'react-table';
import {
  useDateRangeQueryString,
  useManualPagination,
} from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import {
  Detection,
  UserHistory as GraphQLUserHistory,
} from '../../../../shared/generated/graphql';
import { UserHistory } from '../../../entities/queries/types';
import { useGetUserHistoryForEndpointDeviceEntity } from '../../../entities/queries';

export type UserHistoryModalProps = Partial<ModalProps> & {
  edrEntityId: string;
  detectionInfo: Detection;
};
const { entityEDR } = i18n.pages.detections;
const { headings } = entityEDR;
export const UserHistoryModal = ({
  isOpen,
  onClose,
  detectionInfo,
  edrEntityId,
}: UserHistoryModalProps) => {
  const { latest_start_timestamp, tenant } = detectionInfo || {};

  const [userInitialFetch, userSetInitialFetch] = useState<boolean>(false);

  const mapGraphQLUserHistoryToUserHistory = (
    user: GraphQLUserHistory,
  ): UserHistory => ({
    username: user.username ?? '',
    obsts: user.obsts ?? 0,
    data_source: user.data_source ?? '',
    tenant: user.tenant ?? '',
  });

  const dataTableUserColumns: TableColumn<UserHistory>[] = [
    {
      Header: entityEDR.tableHeaders.user,
      accessor: 'username',
    },
    {
      Header: entityEDR.tableHeaders.lastLogin,
      accessor: 'obsts',
      Cell: (props: { value: number }) => {
        const value = props.value ?? 0;
        return <>{moment.unix(value).format('MMMM Do, h:mma')}</>;
      },
    },
  ];
  const { start, end } = useDateRangeQueryString();
  useEffect(() => {
    userSetInitialFetch(true);
  }, [start, end]);
  const {
    sortBy: userSortBy,
    offset: userOffset,
    pageSize: userPageSize,
    pageIndex: userPageIndex,
    pageCount: userPageCount,
    onFetchData: userOnFetchData,
    getPageCount: userGetPageCount,
    getSortsForAPI: userGetSortsForAPI,
  } = useManualPagination<UserHistory>({
    initialFetch: userInitialFetch,
    sortBy: [{ id: 'obsts', desc: true }],
  });

  const {
    loading: getUserHistoryLoading,
    data: getUserHistoryData,
    error: getUserHistoryError,
  } = useGetUserHistoryForEndpointDeviceEntity({
    skip: !isOpen || !edrEntityId,
    variables: {
      tenant,
      as_of: latest_start_timestamp,
      entity: {
        entity_id: edrEntityId,
        entity_type: 'endpoint_device',
      },
      offset: userOffset,
      size: userPageSize,
      sort: userGetSortsForAPI(userSortBy),
    },
    onCompleted: (data: {
      getUserHistoryForEndpointDeviceEntity: import('../../../../shared/generated/graphql').PaginatedUserHistory;
    }) => {
      const { getUserHistoryForEndpointDeviceEntity } = data;
      userSetInitialFetch(false);
      // Fix: Only pass total_items and size to getPageCount
      userGetPageCount({
        total_items: getUserHistoryForEndpointDeviceEntity?.total_items ?? 0,
        size: getUserHistoryForEndpointDeviceEntity?.size ?? 1,
      });
    },
  });
  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      title={headings.userHistory}
      body={
        getUserHistoryLoading || getUserHistoryError ? (
          <GraphQLReqStatus
            error={getUserHistoryError}
            loading={getUserHistoryLoading}
          />
        ) : (
          <Panel>
            <FlushPanelContent>
              <DataTable
                isLined
                pageSize={userPageSize}
                pageCount={userPageCount}
                autoResetPage={false}
                columns={dataTableUserColumns}
                autoResetSortBy={false}
                isPaginationManual={true}
                onFetchData={userOnFetchData}
                data-testid="user-history-table"
                itemCount={
                  getUserHistoryData?.getUserHistoryForEndpointDeviceEntity
                    ?.total_items || 0
                }
                initialState={{
                  pageIndex: userPageIndex,
                  sortBy: userSortBy,
                }}
                data={
                  getUserHistoryData?.getUserHistoryForEndpointDeviceEntity
                    ?.user_histories
                    ? getUserHistoryData.getUserHistoryForEndpointDeviceEntity.user_histories.map(
                        mapGraphQLUserHistoryToUserHistory,
                      )
                    : []
                }
                isPaginable={Boolean(
                  !getUserHistoryLoading &&
                    !getUserHistoryError &&
                    getUserHistoryData?.getUserHistoryForEndpointDeviceEntity
                      ?.user_histories?.length,
                )}
              />
            </FlushPanelContent>
          </Panel>
        )
      }
      footer={
        <ButtonGroup>
          <Button
            variant="solid"
            colorScheme="gray"
            onClick={onClose}
            data-testid="close-button"
          >
            {entityEDR.close}
          </Button>
        </ButtonGroup>
      }
    />
  );
};
